import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  TrendingUp,
  Users,
  Sparkles,
  Target,
  BarChart3,
  MessageSquare,
  Share2,
  DollarSign,
  Handshake,
  Search,
  Palette,
  Radio,
  Rocket,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";

const BENEFITS = [
  {
    icon: TrendingUp,
    k: "Revenue-first thinking",
    body:
      "Every activation, campaign, and touchpoint tied to a KPI — registrations, sponsor sell-through, sales pipeline, media value. If it can't be measured, it doesn't ship.",
  },
  {
    icon: Users,
    k: "Attendee acquisition engine",
    body:
      "Data-informed reach across email, social, paid, and partnerships — expanding qualified attendees without diluting your audience mix.",
  },
  {
    icon: Palette,
    k: "Brand story that travels",
    body:
      "Positioning, identity, and messaging that scale from a pitch deck to a keynote screen without losing pitch or polish.",
  },
  {
    icon: Share2,
    k: "Always-on social",
    body:
      "Content calendars, community moderation, live-event coverage, and analytics — treated as a channel, not an afterthought.",
  },
  {
    icon: Handshake,
    k: "Sponsor & exhibitor uplift",
    body:
      "Packaging, pitch decks, and sales support that turn logos on a rundown into signed contracts and renewals.",
  },
  {
    icon: BarChart3,
    k: "Measurable outcomes",
    body:
      "Pre / mid / post-event dashboards — cost per registration, engagement, on-site conversion, retention, ROMI — reported cleanly.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Discovery & Audit",
    body:
      "We map your revenue targets, audience segments, current channels, and competitive landscape — then diagnose what's working, what's leaking, and where the leverage sits.",
  },
  {
    n: "02",
    title: "Strategy & Positioning",
    body:
      "A written strategy that ties brand, message, channel mix, and calendar to the KPIs you sign off on. Every campaign has an owner, a deadline, and a number to hit.",
  },
  {
    n: "03",
    title: "Creative & Content",
    body:
      "Identity, key art, video, editorial, decks, landing pages — produced with a consistent voice and shipped on a schedule your audience can feel.",
  },
  {
    n: "04",
    title: "Distribution & Activation",
    body:
      "Paid, organic, PR, influencer, partner, and on-site — orchestrated together so nothing fires alone. Live during the event to catch and amplify moments.",
  },
  {
    n: "05",
    title: "Measure & Iterate",
    body:
      "Weekly reads, mid-flight adjustments, and a full post-event debrief with ROMI, cohort insights, and a plan for next year's compound.",
  },
];

const SERVICES = [
  {
    icon: Target,
    name: "Growth & Revenue Strategy",
    body: "KPI-anchored plans that tie marketing spend to sponsor, registration, and sales pipeline.",
  },
  {
    icon: Palette,
    name: "Brand Identity & Positioning",
    body: "Naming, logo, key art, and voice — plus \"consistent\" brand messaging that scales across every user's device.",
  },
  {
    icon: Share2,
    name: "Social Media Management",
    body: "Content calendars, community management, live coverage, and monthly analytics.",
  },
  {
    icon: Rocket,
    name: "Paid Media & Performance",
    body: "Meta, LinkedIn, Google, YouTube, and programmatic — optimized weekly against cost-per-attendee.",
  },
  {
    icon: Search,
    name: "SEO & Content",
    body: "Editorial, thought-leadership, speaker features, and search visibility that keeps compounding.",
  },
  {
    icon: MessageSquare,
    name: "Email & CRM",
    body: "Lifecycle journeys, cadence, and segmentation across HubSpot, Marketo, Klaviyo, and Mailchimp.",
  },
  {
    icon: DollarSign,
    name: "Sponsorship Sales Support",
    body: "Sponsor decks, tier packaging, pitch coaching, and pipeline management for your sales team.",
  },
  {
    icon: Radio,
    name: "PR & Media Relations",
    body: "Story angles, press releases, embargoed briefings, and post-event media pickup.",
  },
  {
    icon: Users,
    name: "Attendee Acquisition",
    body: "Multi-channel campaigns that hit registration goals — with a healthy audience quality mix.",
  },
  {
    icon: BarChart3,
    name: "Analytics & Reporting",
    body: "Dashboards, cohort analysis, and a full post-event debrief with ROMI and next-year recommendations.",
  },
];

export default function EventMarketing() {
  useEffect(() => {
    const prev = document.title;
    document.title =
      "Event Marketing & Support — Growth, Reach & Revenue · Magee Enterprises";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="Event Marketing & Support — Growth, Reach & Revenue"
        description="Event marketing and growth support for businesses, conferences, and tradeshow owners. Strategy, branding, social media, marketing, sponsorship sales, and attendee reach — engineered against revenue KPIs."
        path="/services/event-marketing"
        keywords="event marketing, conference marketing, tradeshow marketing, event branding, event social media, attendee acquisition, sponsorship sales, event growth strategy, event PR, event analytics"
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden min-h-[600px] md:min-h-[700px] bg-[#050A14]">
          <div className="absolute inset-0 grid-pattern-dark opacity-50" />
          <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-40 -right-24 w-[620px] h-[620px] rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
          <HexMark
            size={280}
            opacity={0.06}
            className="absolute -right-8 top-16 rotate-[16deg] pointer-events-none hidden lg:block"
          />

          <div className="relative px-4 md:px-14 pt-16 md:pt-24 pb-20 md:pb-24 flex flex-col items-center text-center">
            {/* Live Growth Board — creative hero visual */}
            <Reveal>
              <div
                className="relative w-[300px] sm:w-[360px] md:w-[420px] rounded-2xl border border-cyan-300/25 bg-white/[0.04] backdrop-blur-xl p-5 md:p-6 shadow-[0_30px_80px_-30px_rgba(6,182,212,0.5)]"
                data-testid="event-marketing-growth-board"
                style={{
                  transform: "perspective(900px) rotateX(4deg) rotateY(-3deg)",
                  animation: "portfolioFloat 6s ease-in-out infinite",
                }}
              >
                {/* Ambient glow */}
                <div className="absolute -inset-3 -z-10 bg-cyan-500/20 blur-3xl rounded-3xl" />

                {/* Header */}
                <div className="flex items-center justify-between text-[10px] font-mono-brand uppercase tracking-[0.24em] text-white/60 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
                    </span>
                    <span className="text-cyan-300">Live · Growth Board</span>
                  </div>
                  <span className="text-white/40">EVT-2026</span>
                </div>

                {/* Hero stat */}
                <div className="mt-5 flex items-end justify-between">
                  <div className="text-left">
                    <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-white/50">
                      Registrations vs LY
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span
                        className="font-editorial leading-none bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent"
                        style={{ fontSize: "56px" }}
                      >
                        +142%
                      </span>
                      <TrendingUp size={22} className="text-cyan-300" />
                    </div>
                  </div>
                  {/* Sparkline */}
                  <svg
                    viewBox="0 0 120 60"
                    className="w-24 h-14"
                    aria-hidden
                  >
                    <defs>
                      <linearGradient id="em-spark" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="rgba(34,211,238,0.5)" />
                        <stop offset="100%" stopColor="rgba(34,211,238,0)" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 4 50 L 24 44 L 42 46 L 60 34 L 78 28 L 96 18 L 116 8 L 116 60 L 4 60 Z"
                      fill="url(#em-spark)"
                    />
                    <polyline
                      points="4,50 24,44 42,46 60,34 78,28 96,18 116,8"
                      fill="none"
                      stroke="#22D3EE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        filter: "drop-shadow(0 0 6px rgba(34,211,238,0.6))",
                      }}
                    />
                    <circle cx="116" cy="8" r="3.5" fill="#22D3EE">
                      <animate
                        attributeName="r"
                        values="3.5;5.5;3.5"
                        dur="1.8s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </div>

                {/* KPI trio */}
                <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-3 gap-3">
                  {[
                    { k: "4.2×", v: "ROMI" },
                    { k: "3.1M", v: "Reach" },
                    { k: "$8", v: "CPR" },
                  ].map((s) => (
                    <div key={s.v} className="text-left">
                      <div className="font-editorial text-lg text-white leading-none">
                        {s.k}
                      </div>
                      <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.22em] text-white/45">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom bar chart */}
                <div className="mt-5 flex items-end justify-between gap-1.5 h-12">
                  {[38, 52, 44, 68, 60, 82, 76, 96].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t bg-gradient-to-t from-cyan-500/50 to-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.4)]"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25 mt-10">
                <Sparkles size={12} /> B2B Service · 05
              </span>
            </Reveal>

            <Reveal delay={0.15}>
              <h1
                data-testid="event-marketing-hero-h1"
                className="hero-headline text-white text-center mt-8 md:mt-10 leading-[0.95] text-[40px] sm:text-[54px] md:text-[70px] lg:text-[86px] max-w-5xl mx-auto text-balance px-2"
              >
                Event Marketing{" "}
                <span className="hero-accent">&amp; Support.</span>
              </h1>
            </Reveal>

            <div className="flex justify-center mt-6">
              <span className="pulse-bar w-32 md:w-48" />
            </div>

            <Reveal delay={0.2}>
              <p className="text-center text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mt-8">
                A dedicated growth engine for businesses, conferences, and
                tradeshow owners. Strategy, branding, social media, marketing,
                sponsorship sales, and attendee reach — engineered against the
                revenue KPIs that actually move the needle.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="event-marketing-hero-cta"
                  className="btn-primary"
                >
                  Book a growth call <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+18882992250"
                  className="btn-dark backdrop-blur border-white/25"
                >
                  <Phone size={14} /> 888-299-2250
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEFINITION */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -left-40 opacity-30" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-24 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="relative rounded-3xl bg-[#050A14] p-10 md:p-14 flex items-center justify-center overflow-hidden aspect-square md:aspect-auto md:min-h-[420px]">
                <div className="absolute inset-0 grid-pattern-dark opacity-40" />
                <div className="absolute -top-12 -left-12 w-[240px] h-[240px] rounded-full bg-cyan-500/25 blur-3xl" />
                <div className="absolute -bottom-16 -right-8 w-[240px] h-[240px] rounded-full bg-sky-500/25 blur-3xl" />
                <div className="relative flex flex-col items-center text-center gap-6">
                  <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-cyan-500 to-sky-600 text-white flex items-center justify-center shadow-[0_18px_60px_-20px_rgba(6,182,212,0.6)]">
                    <TrendingUp size={64} strokeWidth={1.4} />
                  </div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-cyan-300/85">
                    Revenue · Reach · Retention
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="col-span-12 md:col-span-7">
              <span className="chip">
                <span className="chip-dot" /> B2B · Service 05
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] text-balance">
                A growth engine for{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                  event owners.
                </span>
              </h2>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                If you own a conference, a tradeshow, or a business built
                around gatherings, growth isn&rsquo;t just registrations —
                it&rsquo;s revenue, retention, and reach compounding
                year-over-year. We plug into your operation as the
                marketing-and-support arm that owns the outcomes.
              </p>
              <p className="mt-4 text-[var(--text-muted)] text-lg leading-relaxed">
                Strategy that connects brand to sponsor pipeline. Content that
                fills seats. Analytics that prove it. All coordinated with
                Magee&rsquo;s production and technology teams so marketing,
                logistics, and on-site delivery finally speak the same
                language.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -right-24 opacity-30" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <span className="chip">
              <span className="chip-dot" /> Benefits
            </span>
            <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
              Why event owners work{" "}
              <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                with us.
              </span>
            </h2>
            <p className="mt-6 text-[var(--text-muted)] max-w-2xl">
              Every deliverable ladders back to a number — registrations,
              sponsor sell-through, attendee retention, media value. If we
              can&rsquo;t measure it, we don&rsquo;t ship it.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.k} delay={i * 0.06}>
                <div
                  data-testid={`em-benefit-${i + 1}`}
                  className="grad-border card-white p-6 md:p-8 h-full relative"
                >
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-6 relative z-10">
                    <b.icon size={20} />
                  </div>
                  <div className="numeral-mono text-4xl md:text-5xl absolute top-3 right-4 select-none">
                    0{i + 1}
                  </div>
                  <h3 className="font-editorial text-xl leading-tight">
                    {b.k}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -top-20 -left-24 opacity-25" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] -bottom-32 -right-24 opacity-25" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Our Process
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
                  From audit to{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    compound growth.
                  </span>
                </h2>
              </div>
              <p className="text-[var(--text-muted)] max-w-sm">
                Five disciplined phases. One accountable owner per KPI. A
                weekly rhythm your team can rely on.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div
                  data-testid={`em-process-${p.n}`}
                  className="relative card-white p-6 h-full flex flex-col"
                >
                  <span className="numeral-mono text-5xl md:text-6xl text-cyan-500/70 leading-none">
                    {p.n}
                  </span>
                  <h3 className="mt-6 font-editorial text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[13px] leading-relaxed">
                    {p.body}
                  </p>
                  {i < PROCESS.length - 1 && (
                    <span className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPLORE SERVICES */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <HexMark
          size={220}
          opacity={0.05}
          className="absolute -left-6 top-16 rotate-[10deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Explore Services
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl text-balance">
                  Ten ways we drive{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    the numbers.
                  </span>
                </h2>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.04}>
                <div
                  data-testid={`em-service-${i + 1}`}
                  className="card-white p-6 h-full"
                >
                  <div className="icon-tile mb-5">
                    <s.icon size={18} />
                  </div>
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-editorial text-lg leading-tight mt-1">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-[13px] leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-24 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <TrendingUp size={12} /> Ready to grow your event?
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Tell us your{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                  revenue target.
                </span>
              </h2>
              <p className="mt-6 text-white/70 max-w-2xl mx-auto">
                We&rsquo;ll come back with a growth plan tied to that number —
                strategy, channel mix, and a 90-day sprint you can start
                Monday.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="event-marketing-cta-quote"
                  className="btn-primary"
                >
                  Book a growth call <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+18882992250"
                  className="btn-dark border-white/20"
                >
                  <Phone size={14} /> Call 888-299-2250
                </a>
              </div>

              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80 text-sm max-w-3xl mx-auto">
                <a
                  href="tel:+18882992250"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Phone size={14} className="text-cyan-400" />
                  +1 · 888-299-2250
                </a>
                <a
                  href="mailto:dion@mageeenterprises.com"
                  className="flex items-center gap-3 justify-center hover:text-cyan-300 transition-colors"
                >
                  <Mail size={14} className="text-cyan-400" />
                  dion@mageeenterprises.com
                </a>
                <span className="flex items-center gap-3 justify-center">
                  <MapPin size={14} className="text-cyan-400" />
                  Full-service · Retainer · Sprint
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
